import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackFormView } from '../../tracking';
import { LEAD_FORM_ID } from '../../utils/scroll';
import { Button } from '../Button';
import { useLeadForm } from './useLeadForm';
import { TextField, SelectField } from './Field';
import { ConsentCheckbox } from './ConsentCheckbox';
import { ErrorState } from './ErrorState';
import { SuccessPanel } from './SuccessPanel';
const VEHICLE_OPTIONS = ['suv', 'sedan', 'family', 'hybrid-ev',
// Models, so a pick from "Most Wanted" pre-selects cleanly here.
'telluride', 'sportage', 'sorento', 'k5', 'ev', 'carnival', 'not-sure'];
const INTENT_OPTIONS = ['buy', 'lease', 'trade-in-buy', 'not-sure'];
const CREDIT_OPTIONS = ['excellent', 'good', 'fair', 'rebuilding', 'first-time', 'not-sure'];
export function LeadForm() {
  const {
    t
  } = useLanguage();
  const {
    values,
    errors,
    expanded,
    status,
    setField,
    handleSubmit,
    retry
  } = useLeadForm();
  const sectionRef = useRef<HTMLElement>(null);
  const viewed = useRef(false);
  const reduce = useReducedMotion();
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !viewed.current) {
          viewed.current = true;
          trackFormView();
        }
      });
    }, {
      threshold: 0.25
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const options = (keys: string[]) => keys.map(value => ({
    value,
    label: t(`form.opt.${value}`)
  }));
  return <section id={LEAD_FORM_ID} ref={sectionRef} className="relative w-full overflow-hidden bg-ink" data-id="element-291" data-mp="2e9381s0k33n1">
      <div aria-hidden="true" className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-gold/10 blur-[110px]" data-id="element-292" data-mp="297dsbz2e21sm" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-kred/10 blur-[110px]" data-id="element-293" data-mp="1ul70rr45vr9z" />

      <div className="relative mx-auto w-full max-w-2xl px-4 py-16 sm:px-6 md:py-24" data-id="element-294" data-mp="1zi6xhbf4fhof">
        <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-[-0.02em] text-white sm:text-4xl" data-id="element-295" data-mp="1lmv3554vnt81">
          {t('form.title')}
        </h2>
        <p className="mt-3 text-base text-white/60" data-id="element-296" data-mp="2eaqudubogsxf">{t('form.sub')}</p>

        <div className="mt-8" data-id="element-297" data-mp="2ibkioot189qu">
          {status === 'success' ? <SuccessPanel firstName={values.firstName} data-id="element-298" data-mp="2icvqczplm1n8" /> : <form onSubmit={handleSubmit} noValidate className="space-y-5" data-id="element-299" data-mp="24je4h89fwcu1">
              {/* Honeypot — hidden from users, bots that fill it are rejected. */}
              <div className="hidden" aria-hidden="true" data-id="element-300" data-mp="2x81svcphjibt">
                <label htmlFor="company" data-id="element-301" data-mp="2x6gxxlz9dxdf">Company</label>
                <input id="company" name="company" type="text" tabIndex={-1} autoComplete="off" value={values.company} onChange={e => setField('company', e.target.value)} data-id="element-302" data-mp="31ztnpzfoca7r" />
              </div>

              <TextField id="firstName" label={t('form.firstName')} value={values.firstName} onChange={v => setField('firstName', v)} error={errors.firstName} autoComplete="given-name" data-id="element-303" data-mp="3l5rhq5n03ixm" />
              <TextField id="phone" label={t('form.phone')} type="tel" inputMode="tel" value={values.phone} onChange={v => setField('phone', v)} error={errors.phone} autoComplete="tel" data-id="element-304" data-mp="2ofgzg92dod5v" />

              <AnimatePresence initial={false} data-id="element-305" data-mp="cod2dozbo2cw">
                {expanded && <motion.div key="more" initial={reduce ? undefined : {
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={reduce ? undefined : {
              opacity: 0
            }} transition={{
              duration: 0.4,
              ease: [0.22, 1, 0.36, 1]
            }} className="space-y-5" data-id="element-306" data-mp="2ic423hv1omdb">
                    <TextField id="email" label={`${t('form.email')} (${t('form.optional')})`} type="email" inputMode="email" value={values.email} onChange={v => setField('email', v)} error={errors.email} autoComplete="email" data-id="element-307" data-mp="2w9k9avl60k64" />
                    <SelectField id="vehicleInterest" label={t('form.vehicle')} placeholder={t('form.choose')} value={values.vehicleInterest} onChange={v => setField('vehicleInterest', v as never)} options={options(VEHICLE_OPTIONS)} data-id="element-308" data-mp="3k1zp65hiqupp" />
                    <SelectField id="purchaseIntent" label={t('form.intent')} placeholder={t('form.choose')} value={values.purchaseIntent} onChange={v => setField('purchaseIntent', v as never)} options={options(INTENT_OPTIONS)} data-id="element-309" data-mp="14c9a0fhfxqud" />
                    <SelectField id="creditSituation" label={t('form.credit')} placeholder={t('form.choose')} value={values.creditSituation} onChange={v => setField('creditSituation', v as never)} options={options(CREDIT_OPTIONS)} data-id="element-310" data-mp="1s4hgr2kpelkl" />
                  </motion.div>}
              </AnimatePresence>

              <ConsentCheckbox checked={values.consent} onChange={v => setField('consent', v)} error={errors.consent} data-id="element-311" data-mp="2txrjudn06g7h" />

              {status === 'error' && <ErrorState onRetry={retry} data-id="element-312" data-mp="2l4nxffudqt0p" />}

              <Button type="submit" block disabled={status === 'sending'} className="min-h-[58px]" data-id="element-313" data-mp="2p69elddy5tgp">
                {status === 'sending' ? t('form.sending') : t('form.submit')}
              </Button>
              <p className="text-center text-xs text-white/45" data-id="element-314" data-mp="2g6cmf1scmcgw">{t('form.note')}</p>
            </form>}
        </div>
      </div>
    </section>;
}